import { useCallback, useMemo, useState } from "react";

import { Button } from "@/core/ui/atoms";
import {
  adminInputClasses,
  adminSelectClasses,
  AdminPanel,
  AdminMiniList,
  AdminMiniRow,
  Banner,
} from "@/core/ui/molecules";
import { useStore } from "@/core/utils/hooks";
import { ErrorResponse } from "@/core/utils/request";

import { PermissionProfilePresetsStore } from "@/app/access-presets/data_sources/store";
import {
  patchPlanDefinition,
  publishPlanDefinition,
} from "@/app/plans/data_sources/services";
import type {
  APIPlanAccessPresetConfigType,
  APIPlanApprovalLevelPresetType,
  APIPlanDefaultAssignmentType,
  APIPlanDefinitionConfigType,
  APIPlanVendorAccessRulesType,
  APIPlanWorkspaceScopeRulesType,
  PlanRole,
  VendorWorkspaceScope,
  WorkspaceScopeMode,
  WorkspaceScopeValue,
} from "@/core/data_sources/models/APIPlanDefinition";

const ROLES: PlanRole[] = ["owner", "supervisor", "member", "viewer"];
const WORKSPACE_MODES: WorkspaceScopeMode[] = [
  "single_workspace",
  "limited_multi_workspace",
  "unlimited_multi_workspace",
];
const WORKSPACE_SCOPES: WorkspaceScopeValue[] = [
  "all",
  "default_workspace",
  "explicit_workspace",
];
const VENDOR_SCOPES: VendorWorkspaceScope[] = [
  "explicit_workspace_assignment",
  "all_workspaces",
];
const DEFAULT_ASSIGNMENT_KEYS = [
  "tenant_owner",
  "workspace_supervisor",
  "workspace_member",
];

type AccessPresets = NonNullable<APIPlanAccessPresetConfigType>;

const DEFAULT_ACCESS: AccessPresets = {
  permission_profile_presets: [],
  approval_level_presets: [],
  workspace_scope_rules: {
    mode: "single_workspace",
    max_workspaces: 1,
    default_workspace_name: "Main Workspace",
    allow_multi_workspace_users: false,
    allow_master_visibility: true,
    workspace_selector_visible: true,
  },
  vendor_access_rules: {
    vendors_enabled: false,
    allow_vendor_users: false,
    allow_vendor_admins: false,
    allow_workspace_vendor_creation: false,
    vendor_workspace_scope: "explicit_workspace_assignment",
  },
  default_assignments: {},
};

export function AccessPresetsEditor({
  initialConfig,
  onSaved,
  planId,
}: {
  readonly initialConfig?: APIPlanDefinitionConfigType;
  readonly onSaved: () => void;
  readonly planId: string | number;
}) {
  const { state } = useStore(PermissionProfilePresetsStore.instance);
  const { hooks } = useStore(PermissionProfilePresetsStore.instance);
  hooks.usePermissionProfilePresetsStoreService({ page: 1, page_size: 200 });
  const catalog = state?.list ?? [];
  const activePresets = useMemo(
    () => catalog.filter((p) => p.getData().status === "active"),
    [catalog],
  );

  const [access, setAccess] = useState<AccessPresets>(() => ({
    ...DEFAULT_ACCESS,
    ...(initialConfig?.access_presets ?? {}),
  }));
  const [busy, setBusy] = useState(false);
  const [publishErrors, setPublishErrors] = useState<string[]>([]);
  const [savedNote, setSavedNote] = useState<string | null>(null);

  const addedCodes = useMemo(
    () => (access.permission_profile_presets ?? []).map((p) => p.preset_code),
    [access.permission_profile_presets],
  );
  const presetMeta = useCallback(
    (code: string) =>
      catalog.find((p) => p.getData().preset_code === code)?.getData(),
    [catalog],
  );

  const setSection = useCallback(
    <K extends keyof AccessPresets>(key: K, value: AccessPresets[K]) => {
      setAccess((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const addPreset = useCallback(
    (code: string) => {
      if (!code || addedCodes.includes(code)) return;
      setSection("permission_profile_presets", [
        ...(access.permission_profile_presets ?? []),
        { preset_code: code, auto_install: true },
      ]);
    },
    [addedCodes, access.permission_profile_presets, setSection],
  );
  const removePreset = useCallback(
    (code: string) => {
      setSection(
        "permission_profile_presets",
        (access.permission_profile_presets ?? []).filter(
          (p) => p.preset_code !== code,
        ),
      );
    },
    [access.permission_profile_presets, setSection],
  );
  const toggleAutoInstall = useCallback(
    (code: string, value: boolean) => {
      setSection(
        "permission_profile_presets",
        (access.permission_profile_presets ?? []).map((p) =>
          p.preset_code === code ? { ...p, auto_install: value } : p,
        ),
      );
    },
    [access.permission_profile_presets, setSection],
  );

  const addApprovalLevel = useCallback(
    (level: number, label: string, description: string) => {
      if (!level) return;
      const exists = (access.approval_level_presets ?? []).some(
        (a) => a.level === level,
      );
      if (exists) {
        setPublishErrors([`Approval level ${level} already exists.`]);
        return;
      }
      setSection("approval_level_presets", [
        ...(access.approval_level_presets ?? []),
        { level, label, description },
      ]);
    },
    [access.approval_level_presets, setSection, setPublishErrors],
  );
  const removeApprovalLevel = useCallback(
    (level: number) => {
      setSection(
        "approval_level_presets",
        (access.approval_level_presets ?? []).filter((a) => a.level !== level),
      );
    },
    [access.approval_level_presets, setSection],
  );

  const setWorkspaceRules = useCallback(
    (patch: Partial<APIPlanWorkspaceScopeRulesType>) => {
      setSection("workspace_scope_rules", {
        ...(access.workspace_scope_rules ??
          DEFAULT_ACCESS.workspace_scope_rules!),
        ...patch,
      });
    },
    [access.workspace_scope_rules, setSection],
  );
  const setVendorRules = useCallback(
    (patch: Partial<APIPlanVendorAccessRulesType>) => {
      setSection("vendor_access_rules", {
        ...(access.vendor_access_rules ?? DEFAULT_ACCESS.vendor_access_rules!),
        ...patch,
      });
    },
    [access.vendor_access_rules, setSection],
  );

  const setAssignment = useCallback(
    (key: string, patch: Partial<APIPlanDefaultAssignmentType>) => {
      const current = access.default_assignments?.[key] ?? {
        role: "member" as PlanRole,
        permission_profile_preset_code: "",
        workspace_scope: "default_workspace" as WorkspaceScopeValue,
      };
      setSection("default_assignments", {
        ...(access.default_assignments ?? {}),
        [key]: { ...current, ...patch },
      });
    },
    [access.default_assignments, setSection],
  );

  const buildConfig = useCallback(
    (): APIPlanDefinitionConfigType => ({
      ...(initialConfig ?? {}),
      access_presets: access,
    }),
    [initialConfig, access],
  );

  const handleSaveDraft = useCallback(async () => {
    setBusy(true);
    setPublishErrors([]);
    try {
      const resp = await patchPlanDefinition(planId, { config: buildConfig() })
        .response;
      if ((resp.data as ErrorResponse).error) throw resp.data;
      setSavedNote("Draft saved");
      onSaved();
    } catch (err) {
      setPublishErrors([
        (err as ErrorResponse).error?.message || "Save failed",
      ]);
    } finally {
      setBusy(false);
    }
  }, [planId, buildConfig, onSaved]);

  const handlePublish = useCallback(async () => {
    setBusy(true);
    setPublishErrors([]);
    try {
      const resp = await publishPlanDefinition(planId).response;
      if ((resp.data as ErrorResponse).error) throw resp.data;
      setSavedNote("Plan published");
      onSaved();
    } catch (err) {
      setPublishErrors(parsePublishErrors(err as ErrorResponse));
    } finally {
      setBusy(false);
    }
  }, [planId, onSaved]);

  const availableToAdd = activePresets.filter(
    (p) => !addedCodes.includes(p.getData().preset_code ?? ""),
  );

  return (
    <div>
      <Banner tone="ok">
        <strong>Access Presets are installed at tenant creation.</strong> Select
        presets from the global catalog and define default assignments. Plan
        Engine only selects existing presets; it never creates roles or presets.
      </Banner>

      <section className="mb-4">
        <h3 className="mb-2 text-[15px] font-extrabold text-ink">
          Permission Profile Presets
        </h3>
        <AdminPanel className="p-[18px]">
          <div className="mb-3 flex items-center gap-2">
            <select
              className={adminSelectClasses}
              onChange={(e) => {
                addPreset(e.target.value);
                e.target.value = "";
              }}
              value=""
            >
              <option value="">+ Add preset from catalog…</option>
              {availableToAdd.map((p) => (
                <option
                  key={p.getData().preset_code}
                  value={p.getData().preset_code}
                >
                  {p.getData().name} ({p.getData().preset_code})
                </option>
              ))}
            </select>
          </div>
          <AdminMiniList>
            {(access.permission_profile_presets ?? []).map((p) => {
              const meta = presetMeta(p.preset_code);
              const resolved = !!meta;
              return (
                <AdminMiniRow key={p.preset_code}>
                  <span>
                    <strong>{meta?.name ?? p.preset_code}</strong>
                    <span className="block text-xs text-ink-muted">
                      {p.preset_code}
                      {resolved ? "" : " · unresolved (not in catalog)"}
                    </span>
                  </span>
                  <span className="flex items-center gap-3">
                    <label className="flex items-center gap-1.5 text-xs text-ink-muted">
                      <input
                        checked={!!p.auto_install}
                        onChange={(e) =>
                          toggleAutoInstall(p.preset_code, e.target.checked)
                        }
                        type="checkbox"
                      />
                      auto_install
                    </label>
                    <Button
                      onClick={() => removePreset(p.preset_code)}
                      size="sm"
                      variant="danger"
                    >
                      Remove
                    </Button>
                  </span>
                </AdminMiniRow>
              );
            })}
            {(access.permission_profile_presets ?? []).length === 0 ? (
              <li className="px-0 py-2 text-xs text-ink-muted">
                No presets selected yet.
              </li>
            ) : null}
          </AdminMiniList>
        </AdminPanel>
      </section>

      <ApprovalLevelsSection
        levels={access.approval_level_presets ?? []}
        onAdd={addApprovalLevel}
        onRemove={removeApprovalLevel}
      />

      <section className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-2">
        <WorkspaceScopeSection
          rules={
            access.workspace_scope_rules ??
            DEFAULT_ACCESS.workspace_scope_rules!
          }
          onChange={setWorkspaceRules}
        />
        <VendorSection
          rules={
            access.vendor_access_rules ?? DEFAULT_ACCESS.vendor_access_rules!
          }
          onChange={setVendorRules}
        />
      </section>

      <DefaultAssignmentsSection
        assignments={access.default_assignments ?? {}}
        presetCodes={addedCodes}
        onChange={setAssignment}
      />

      {savedNote ? (
        <Banner tone="ok">
          <strong>{savedNote}</strong>
        </Banner>
      ) : null}
      {publishErrors.length > 0 ? (
        <Banner tone="error">
          <strong>Validation failed:</strong>
          <ul className="ml-4 list-disc">
            {publishErrors.map((e, i) => (
              <li key={i}>{e}</li>
            ))}
          </ul>
        </Banner>
      ) : null}

      <div className="mt-4 flex justify-end gap-2">
        <Button disabled={busy} onClick={handleSaveDraft}>
          Save Draft
        </Button>
        <Button disabled={busy} onClick={handlePublish} variant="primary">
          Publish
        </Button>
      </div>
    </div>
  );
}

function ApprovalLevelsSection({
  levels,
  onAdd,
  onRemove,
}: {
  readonly levels: APIPlanApprovalLevelPresetType[];
  readonly onAdd: (level: number, label: string, description: string) => void;
  readonly onRemove: (level: number) => void;
}) {
  const [level, setLevel] = useState("");
  const [label, setLabel] = useState("");
  return (
    <section className="mb-4">
      <h3 className="mb-2 text-[15px] font-extrabold text-ink">
        Approval Levels
      </h3>
      <AdminPanel className="p-[18px]">
        <div className="mb-3 flex flex-wrap items-end gap-2">
          <label className="flex flex-col text-xs font-extrabold text-ink-muted">
            Level
            <input
              className={`${adminInputClasses} w-20`}
              onChange={(e) => setLevel(e.target.value)}
              type="number"
              value={level}
            />
          </label>
          <label className="flex flex-1 flex-col text-xs font-extrabold text-ink-muted">
            Label
            <input
              className={adminInputClasses}
              onChange={(e) => setLabel(e.target.value)}
              value={label}
            />
          </label>
          <Button
            onClick={() => {
              onAdd(Number(level), label, "");
              setLevel("");
              setLabel("");
            }}
            size="sm"
            variant="primary"
          >
            Add level
          </Button>
        </div>
        <AdminMiniList>
          {levels.map((a) => (
            <AdminMiniRow key={a.level}>
              <span>
                <strong>L{a.level}</strong> · {a.label}
              </span>
              <Button
                onClick={() => onRemove(a.level)}
                size="sm"
                variant="danger"
              >
                Remove
              </Button>
            </AdminMiniRow>
          ))}
          {levels.length === 0 ? (
            <li className="px-0 py-2 text-xs text-ink-muted">
              No approval levels defined.
            </li>
          ) : null}
        </AdminMiniList>
      </AdminPanel>
    </section>
  );
}

function WorkspaceScopeSection({
  rules,
  onChange,
}: {
  readonly rules: APIPlanWorkspaceScopeRulesType;
  readonly onChange: (patch: Partial<APIPlanWorkspaceScopeRulesType>) => void;
}) {
  return (
    <AdminPanel className="p-[18px]">
      <h3 className="mb-3 text-[15px] font-extrabold text-ink">
        Workspace Scope Rules
      </h3>
      <label className="mb-3 block">
        <span className="mb-1.5 block text-xs font-extrabold text-ink-muted">
          Mode
        </span>
        <select
          className={adminSelectClasses}
          onChange={(e) =>
            onChange({ mode: e.target.value as WorkspaceScopeMode })
          }
          value={rules.mode}
        >
          {WORKSPACE_MODES.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>
      </label>
      <label className="mb-3 block">
        <span className="mb-1.5 block text-xs font-extrabold text-ink-muted">
          Max workspaces
        </span>
        <input
          className={adminInputClasses}
          onChange={(e) => onChange({ max_workspaces: Number(e.target.value) })}
          type="number"
          value={rules.max_workspaces}
        />
      </label>
      <label className="mb-3 block">
        <span className="mb-1.5 block text-xs font-extrabold text-ink-muted">
          Default workspace name
        </span>
        <input
          className={adminInputClasses}
          onChange={(e) => onChange({ default_workspace_name: e.target.value })}
          value={rules.default_workspace_name}
        />
      </label>
      <CheckRow
        checked={rules.allow_multi_workspace_users}
        label="Allow multi-workspace users"
        onChange={(v) => onChange({ allow_multi_workspace_users: v })}
      />
      <CheckRow
        checked={rules.allow_master_visibility}
        label="Allow master visibility"
        onChange={(v) => onChange({ allow_master_visibility: v })}
      />
      <CheckRow
        checked={rules.workspace_selector_visible}
        label="Workspace selector visible"
        onChange={(v) => onChange({ workspace_selector_visible: v })}
      />
    </AdminPanel>
  );
}

function VendorSection({
  rules,
  onChange,
}: {
  readonly rules: APIPlanVendorAccessRulesType;
  readonly onChange: (patch: Partial<APIPlanVendorAccessRulesType>) => void;
}) {
  return (
    <AdminPanel className="p-[18px]">
      <h3 className="mb-3 text-[15px] font-extrabold text-ink">
        Vendor Access Rules
      </h3>
      <CheckRow
        checked={rules.vendors_enabled}
        label="Vendors enabled"
        onChange={(v) => onChange({ vendors_enabled: v })}
      />
      <CheckRow
        checked={rules.allow_vendor_users}
        label="Allow vendor users"
        onChange={(v) => onChange({ allow_vendor_users: v })}
      />
      <CheckRow
        checked={rules.allow_vendor_admins}
        label="Allow vendor admins"
        onChange={(v) => onChange({ allow_vendor_admins: v })}
      />
      <CheckRow
        checked={rules.allow_workspace_vendor_creation}
        label="Allow workspace vendor creation"
        onChange={(v) => onChange({ allow_workspace_vendor_creation: v })}
      />
      <label className="mt-2 block">
        <span className="mb-1.5 block text-xs font-extrabold text-ink-muted">
          Vendor workspace scope
        </span>
        <select
          className={adminSelectClasses}
          onChange={(e) =>
            onChange({
              vendor_workspace_scope: e.target.value as VendorWorkspaceScope,
            })
          }
          value={rules.vendor_workspace_scope}
        >
          {VENDOR_SCOPES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </label>
      {rules.vendors_enabled ? (
        <p className="mt-2 text-xs text-amber-700">
          Requires VENDORS_MODULE enabled at plan level.
        </p>
      ) : null}
    </AdminPanel>
  );
}

function DefaultAssignmentsSection({
  assignments,
  onChange,
  presetCodes,
}: {
  readonly assignments: Record<string, APIPlanDefaultAssignmentType>;
  readonly onChange: (
    key: string,
    patch: Partial<APIPlanDefaultAssignmentType>,
  ) => void;
  readonly presetCodes: string[];
}) {
  const keys = Array.from(
    new Set([...DEFAULT_ASSIGNMENT_KEYS, ...Object.keys(assignments)]),
  );
  return (
    <section className="mb-4">
      <h3 className="mb-2 text-[15px] font-extrabold text-ink">
        Default Assignments
      </h3>
      <div className="grid grid-cols-1 gap-3">
        {keys.map((key) => {
          const a = assignments[key];
          return (
            <AdminPanel className="p-[18px]" key={key}>
              <div className="mb-2 text-[13px] font-extrabold text-ink">
                {key}
              </div>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
                <label>
                  <span className="mb-1.5 block text-xs font-extrabold text-ink-muted">
                    Role
                  </span>
                  <select
                    className={adminSelectClasses}
                    onChange={(e) =>
                      onChange(key, { role: e.target.value as PlanRole })
                    }
                    value={a?.role ?? "member"}
                  >
                    {ROLES.map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  <span className="mb-1.5 block text-xs font-extrabold text-ink-muted">
                    Preset
                  </span>
                  <select
                    className={adminSelectClasses}
                    onChange={(e) =>
                      onChange(key, {
                        permission_profile_preset_code: e.target.value,
                      })
                    }
                    value={a?.permission_profile_preset_code ?? ""}
                  >
                    <option value="">— select —</option>
                    {presetCodes.map((code) => (
                      <option key={code} value={code}>
                        {code}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  <span className="mb-1.5 block text-xs font-extrabold text-ink-muted">
                    Approval level
                  </span>
                  <input
                    className={adminInputClasses}
                    onChange={(e) =>
                      onChange(key, {
                        approval_level: e.target.value
                          ? Number(e.target.value)
                          : null,
                      })
                    }
                    placeholder="optional"
                    type="number"
                    value={a?.approval_level ?? ""}
                  />
                </label>
                <label>
                  <span className="mb-1.5 block text-xs font-extrabold text-ink-muted">
                    Workspace scope
                  </span>
                  <select
                    className={adminSelectClasses}
                    onChange={(e) =>
                      onChange(key, {
                        workspace_scope: e.target.value as WorkspaceScopeValue,
                      })
                    }
                    value={a?.workspace_scope ?? "default_workspace"}
                  >
                    {WORKSPACE_SCOPES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            </AdminPanel>
          );
        })}
      </div>
    </section>
  );
}

function CheckRow({
  checked,
  label,
  onChange,
}: {
  readonly checked: boolean;
  readonly label: string;
  readonly onChange: (value: boolean) => void;
}) {
  return (
    <label className="mb-2 flex items-center gap-2 text-[13px] text-ink">
      <input
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        type="checkbox"
      />
      {label}
    </label>
  );
}

function parsePublishErrors(err: ErrorResponse): string[] {
  const raw = err.error?.message ?? "";
  try {
    const parsed = JSON.parse(raw) as Array<{
      field: string;
      code: string;
      message: string;
    }>;
    if (Array.isArray(parsed)) {
      return parsed.map((e) => `${e.field}: ${e.code} — ${e.message}`);
    }
  } catch {
    /* not a flat error list */
  }
  if (err.status === 400)
    return ["Publish validation failed. Check the access presets."];
  return [raw || "Publish failed"];
}
